import React, { useState, useRef, useEffect } from "react";
import { Send, Sparkles, Bot, User as UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/apiClient";

const PROMPTS = [
  "Prix moyen au m² pour couler une chape fluide ?",
  "Comment savoir si une fissure en escalier est structurelle ?",
  "Aides MaPrimeRénov' pour une pompe à chaleur en 2026 ?",
  "Un acompte de 40% à la commande, est-ce normal ?",
];

const sid = "chat-" + Math.random().toString(36).slice(2, 10);

export const ChatAssistant = () => {
  const [messages, setMessages] = useState([
    { id: 0, role: "assistant", content: "Bonjour 👋 Je suis votre expert bâtiment ID-BAT PRO. Posez-moi une question sur vos travaux, les normes DTU, les prix ou les aides." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);
  const idRef = useRef(1);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    const uId = idRef.current++;
    const aId = idRef.current++;
    setMessages((m) => [...m, { id: uId, role: "user", content: msg }, { id: aId, role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg, session_id: sid }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: acc };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: "Erreur de connexion à l'assistant. Réessayez." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="rounded-3xl bg-[#121A29] border border-white/10 overflow-hidden flex flex-col h-[560px]" data-testid="ai-chat-widget">
      <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3 bg-white/5">
        <div className="w-10 h-10 rounded-full bg-[#0D6EFD]/20 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-[#00C3A7]" />
        </div>
        <div>
          <p className="text-white font-semibold text-sm">Assistant IA Expert Bâtiment</p>
          <p className="text-[#00C3A7] text-xs flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#00C3A7] idb-pulse-dot" /> En ligne · DTU & RE2020
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 idb-scroll" data-testid="ai-chat-messages-container">
        {messages.map((m, i) => (
          <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
              m.role === "user" ? "bg-[#0D6EFD]" : "bg-white/10"
            }`}>
              {m.role === "user" ? <UserIcon className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-[#00C3A7]" />}
            </div>
            <div className={`rounded-2xl px-4 py-2.5 max-w-[80%] text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user" ? "bg-[#0D6EFD] text-white" : "bg-white/5 text-white/90 border border-white/5"
            }`}>
              {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {messages.length <= 1 && (
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          {PROMPTS.map((p) => (
            <button key={p} onClick={() => send(p)}
              className="text-xs text-white/70 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 transition-colors">
              {p}
            </button>
          ))}
        </div>
      )}

      <div className="p-4 border-t border-white/10 flex gap-2">
        <Input
          data-testid="ai-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Posez votre question bâtiment..."
          className="bg-white/5 border-white/10 text-white placeholder:text-white/40 rounded-full"
        />
        <Button onClick={() => send()} disabled={streaming} data-testid="ai-chat-submit"
          className="bg-[#0D6EFD] hover:bg-[#0b5ed7] rounded-full w-11 h-11 p-0 shrink-0">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
